// apps/web/src/features/crm/leads/useLeadBinding.ts
//
// CRM v4 M3 (#2244 / #2247 M6-partial) lead->client binding hooks. Thin wrappers
// over the merged M3 endpoints, mirroring the `usePatchLead` shape (a
// schema-validated `useApiClient` call plus `leadsKeys` invalidation) so the
// future full-page 3-zone card consumes the SAME hooks the current
// `LeadClientBindingSection` uses today.
//   - GET  /leads/:id/match-candidates?clientId=  -> the pre-filled bind proposal.
//   - POST /leads/:id/bind                          -> the atomic one-confirmation bind.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LeadListItemSchema,
  MatchCandidatesResponseSchema,
  type BindLeadInput,
  type LeadListItem,
  type MatchCandidatesResponse,
} from '@magimp/shared';
import { leadsKeys, useApiClient } from '@magimp/shared-hooks';

/** Query key for a lead's bind proposal against one target client. Nested under
 * the lead's detail prefix so a lead invalidation flushes stale proposals too. */
export const leadMatchCandidatesKey = (leadId: string, clientId: string) =>
  ['leads', 'detail', leadId, 'match-candidates', clientId] as const;

/**
 * @aiPurpose fetch the semi-automatic bind proposal (#2244 FR-2) comparing the
 *   lead's registry identifiers to a target client's contacts. `enabled` gates
 *   the fetch on a chosen client, so the dialog only queries once a target
 *   exists.
 */
export function useMatchCandidates(leadId: string, clientId: string | null) {
  const client = useApiClient();
  return useQuery<MatchCandidatesResponse>({
    queryKey: leadMatchCandidatesKey(leadId, clientId ?? ''),
    queryFn: () =>
      client.get<MatchCandidatesResponse>(
        `/leads/${leadId}/match-candidates`,
        { query: { clientId: clientId! } },
        MatchCandidatesResponseSchema,
      ),
    enabled: clientId != null,
  });
}

/**
 * @aiPurpose confirm the lead->client bind in one call (#2244 FR-3). The bind is
 *   a discriminated union on `mode` (matched | create). The refreshed bound
 *   `LeadListItem` comes back; invalidate the lead list + detail so every reader
 *   (list rows, detail card, binding section) re-reads the bound state.
 */
export function useBindLead(leadId: string) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<LeadListItem, unknown, BindLeadInput>({
    mutationFn: (input) =>
      client.post<LeadListItem>(`/leads/${leadId}/bind`, input, {}, LeadListItemSchema),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leadsKeys.all });
      void qc.invalidateQueries({ queryKey: leadsKeys.detail(leadId) });
    },
  });
}
